import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FaCheck,
  FaClipboardCheck,
  FaHistory,
  FaPlus,
  FaQrcode,
  FaSearch,
  FaSync,
  FaUserCheck,
} from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import CampCheckInScanner from '../../components/admin/CampCheckInScanner';
import CampFallbackRegistrationModal from '../../components/admin/CampFallbackRegistrationModal';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateTitle,
  Select,
} from '../../styles/AdminStyles';
import {
  fetchAdminJson,
  formatDateTime,
  getAdminHeaders,
} from './campAdminShared';

const CampCheckInsPage = () => {
  const [checkInOverview, setCheckInOverview] = useState(null);
  const [selectedDayKey, setSelectedDayKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [nameSearch, setNameSearch] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationNotice, setRegistrationNotice] = useState('');

  const loadPageData = async (dayKey = selectedDayKey) => {
    try {
      setError('');
      const query = dayKey ? `?dayKey=${encodeURIComponent(dayKey)}` : '';
      const [overviewData, registrationsData] = await Promise.all([
        fetchAdminJson(`/camp-registration/check-in-overview${query}`, {
          headers: getAdminHeaders(),
        }),
        fetchAdminJson('/camp-registration/all', {
          headers: getAdminHeaders(),
        }),
      ]);
      setCheckInOverview(overviewData);
      setSelectedDayKey(overviewData.selectedDay?.dayKey || '');
      setRegistrations(registrationsData);
    } catch (loadError) {
      console.error('Error loading check-in overview:', loadError);
      setError(loadError.message || 'Failed to load check-in overview.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadPageData(selectedDayKey);
  };

  const handleDayChange = async (event) => {
    const nextDay = event.target.value;
    setSelectedDayKey(nextDay);
    setRefreshing(true);
    await loadPageData(nextDay);
  };

  const handleScan = async (qrPayload) => {
    try {
      setProcessing(true);
      setError('');
      const result = await fetchAdminJson('/camp-registration/check-in/scan', {
        method: 'POST',
        headers: getAdminHeaders(true),
        body: JSON.stringify({
          qrPayload,
          dayKey: selectedDayKey || undefined,
        }),
      });

      setLastResult(result);
      await loadPageData(result.day?.dayKey || selectedDayKey);
    } catch (scanError) {
      console.error('Error processing check-in:', scanError);
      setError(scanError.message || 'Failed to process check-in.');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualCheckIn = async (participant) => {
    try {
      setProcessing(true);
      setError('');
      const result = await fetchAdminJson('/camp-registration/check-in/scan', {
        method: 'POST',
        headers: getAdminHeaders(true),
        body: JSON.stringify({
          registrationId: participant.registrationId,
          participantKey: participant.participantKey,
          dayKey: selectedDayKey || undefined,
        }),
      });

      setLastResult(result);
      await loadPageData(result.day?.dayKey || selectedDayKey);
    } catch (manualError) {
      console.error('Error processing manual check-in:', manualError);
      setError(manualError.message || 'Failed to process manual check-in.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCamperRegistered = async (result, payload) => {
    const primaryName = result?.fullName || payload?.fullName || '';
    setRegistrationNotice(
      `${primaryName} has been registered. You can check the camper in now.`,
    );
    setNameSearch(primaryName);
    await loadPageData(selectedDayKey);
  };

  const searchableParticipants = useMemo(() => {
    const confirmedRegistrations = registrations.filter(
      (registration) => registration.status === 'confirmed',
    );

    return confirmedRegistrations.flatMap((registration) => {
      const participants = [
        {
          registrationId: registration._id,
          registrationNumber: registration.registrationNumber,
          registrationName: registration.fullName,
          participantKey: 'primary',
          participantName: registration.fullName,
          roleLabel:
            registration.registrationType === 'child'
              ? 'Child Registrant'
              : 'Registrant',
          branch: registration.branch,
          roomType: registration.roomType,
          phone: registration.phone,
        },
      ];

      if (registration.hasSpouse && registration.spouseInfo?.name) {
        participants.push({
          registrationId: registration._id,
          registrationNumber: registration.registrationNumber,
          registrationName: registration.fullName,
          participantKey: 'spouse',
          participantName: registration.spouseInfo.name,
          roleLabel: 'Spouse',
          branch: registration.branch,
          roomType: registration.roomType,
          phone: registration.spouseInfo.phone || registration.phone,
        });
      }

      (registration.children || []).forEach((child, index) => {
        if (!child?.name) {
          return;
        }

        participants.push({
          registrationId: registration._id,
          registrationNumber: registration.registrationNumber,
          registrationName: registration.fullName,
          participantKey: `child-${index}`,
          participantName: child.name,
          roleLabel: `Child ${index + 1}`,
          branch: registration.branch,
          roomType: registration.roomType,
          phone: registration.phone,
        });
      });

      return participants;
    });
  }, [registrations]);

  const manualMatches = useMemo(() => {
    const term = nameSearch.trim().toLowerCase();

    if (term.length < 2) {
      return [];
    }

    return searchableParticipants
      .filter((participant) => {
        const fields = [
          participant.participantName,
          participant.registrationName,
          participant.registrationNumber,
          participant.phone,
        ]
          .filter(Boolean)
          .map((value) => value.toLowerCase());

        return fields.some((value) => value.includes(term));
      })
      .sort((first, second) =>
        first.participantName.localeCompare(second.participantName),
      )
      .slice(0, 12);
  }, [nameSearch, searchableParticipants]);

  if (loading) {
    return (
      <AdminLayout title="Camp Check-Ins" subtitle="Loading...">
        <LoadingState>Loading camp check-ins...</LoadingState>
      </AdminLayout>
    );
  }

  const activeDaySummary = checkInOverview?.daySummaries?.find(
    (day) => day.dayKey === selectedDayKey,
  );

  return (
    <AdminLayout
      title="Camp Check-Ins"
      subtitle="Scan camper QR codes and review attendance by camp day."
    >
      {error && (
        <Alert $type="error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </Alert>
      )}

      <ToolbarCard>
        <CardBody>
          <Toolbar>
            <DayPicker>
              <label htmlFor="camp-day">Active Day</label>
              <Select
                id="camp-day"
                value={selectedDayKey}
                onChange={handleDayChange}
              >
                {(checkInOverview?.campDays || []).map((day) => (
                  <option key={day.dayKey} value={day.dayKey}>
                    {day.dayLabel} - {day.dateLabel}
                  </option>
                ))}
              </Select>
            </DayPicker>

            <Button onClick={handleRefresh} disabled={refreshing || processing}>
              <FaSync className={refreshing ? 'spin' : ''} /> Refresh
            </Button>

            <Button
              $variant="secondary"
              onClick={() => setShowRegisterModal(true)}
              disabled={processing}
            >
              <FaPlus /> Register Camper
            </Button>
          </Toolbar>
        </CardBody>
      </ToolbarCard>

      {registrationNotice && (
        <Alert $type="success" style={{ marginBottom: '1.5rem' }}>
          {registrationNotice}
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard>
          <strong>{activeDaySummary?.checkedInCount || 0}</strong>
          <span>Checked in for this day</span>
        </SummaryCard>
        <SummaryCard>
          <strong>{activeDaySummary?.outstandingCount || 0}</strong>
          <span>Still expected</span>
        </SummaryCard>
        <SummaryCard>
          <strong>{checkInOverview?.summary?.totalParticipants || 0}</strong>
          <span>Total campers</span>
        </SummaryCard>
      </SummaryGrid>

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardHeader>
          <CardTitle>
            <FaQrcode /> QR Scanner
          </CardTitle>
        </CardHeader>
        <CardBody>
          <CampCheckInScanner onScan={handleScan} processing={processing} />
        </CardBody>
      </Card>

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardHeader>
          <CardTitle>
            <FaSearch /> Name Search Check-In
          </CardTitle>
        </CardHeader>
        <CardBody>
          <SearchHelp>
            If a camper does not have a QR code, search by name, choose the
            correct match, and check them in for the active day.
          </SearchHelp>

          <ManualSearchBox>
            <FaSearch />
            <input
              type="text"
              placeholder="Search by camper name, registration number, or phone..."
              value={nameSearch}
              onChange={(event) => setNameSearch(event.target.value)}
              disabled={processing}
            />
          </ManualSearchBox>

          {nameSearch.trim().length > 0 && nameSearch.trim().length < 2 && (
            <SearchHint>Type at least 2 characters to search.</SearchHint>
          )}

          {manualMatches.length > 0 && (
            <ManualResults>
              {manualMatches.map((participant) => (
                <ManualResultCard
                  key={`${participant.registrationId}-${participant.participantKey}`}
                >
                  <div>
                    <strong>{participant.participantName}</strong>
                    <span>
                      {participant.roleLabel} on {participant.registrationName}
                    </span>
                    <small>
                      {participant.registrationNumber} •{' '}
                      {participant.roomType || 'No room'} •{' '}
                      {participant.phone || 'No phone'}
                    </small>
                  </div>
                  <Button
                    onClick={() => handleManualCheckIn(participant)}
                    disabled={processing}
                  >
                    <FaCheck /> Check In Camper
                  </Button>
                </ManualResultCard>
              ))}
            </ManualResults>
          )}

          {nameSearch.trim().length >= 2 && manualMatches.length === 0 && (
            <NoMatchBlock>
              <SearchHint>No campers matched that search.</SearchHint>
              <Button
                type="button"
                onClick={() => setShowRegisterModal(true)}
                disabled={processing}
              >
                <FaPlus /> Register Camper
              </Button>
            </NoMatchBlock>
          )}
        </CardBody>
      </Card>

      {lastResult && (
        <ResultCard $status={lastResult.status}>
          <ResultIcon>
            <FaUserCheck />
          </ResultIcon>
          <div>
            <ResultTitle>
              {lastResult.status === 'already_checked_in'
                ? 'Camper was already checked in'
                : 'Camper checked in successfully'}
            </ResultTitle>
            <ResultText>
              {lastResult.participant?.participantName} for {lastResult.day?.dayLabel}
            </ResultText>
            <ResultMeta>
              {lastResult.registration?.registrationNumber} •{' '}
              {lastResult.registration?.roomType || 'No room assigned'} •{' '}
              {formatDateTime(
                lastResult.checkIn?.checkedInAt || new Date().toISOString(),
              )}
            </ResultMeta>
          </div>
        </ResultCard>
      )}

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardHeader>
          <CardTitle>
            <FaClipboardCheck /> Daily Progress
          </CardTitle>
        </CardHeader>
        <CardBody>
          <DayGrid>
            {(checkInOverview?.daySummaries || []).map((day) => (
              <DayCard
                key={day.dayKey}
                $active={day.dayKey === selectedDayKey}
                onClick={() =>
                  handleDayChange({ target: { value: day.dayKey } })
                }
              >
                <h3>{day.dayLabel}</h3>
                <p>{day.dateLabel}</p>
                <strong>
                  {day.checkedInCount}/{day.totalParticipants}
                </strong>
                <span>{day.outstandingCount} remaining</span>
              </DayCard>
            ))}
          </DayGrid>
        </CardBody>
      </Card>

      <Columns>
        <Card>
          <CardHeader>
            <CardTitle>Checked In For {activeDaySummary?.dayLabel || 'Day'}</CardTitle>
          </CardHeader>
          <CardBody>
            {checkInOverview?.checkedInParticipants?.length ? (
              <ListGrid>
                {checkInOverview.checkedInParticipants.map((entry) => (
                  <PersonCard key={`${entry.dayKey}-${entry.participantKey}`}>
                    <strong>{entry.participantName}</strong>
                    <span>{entry.roleLabel}</span>
                    <small>
                      {entry.registrationNumber} • {entry.roomType || 'No room'}
                    </small>
                    <small>{formatDateTime(entry.checkedInAt)}</small>
                  </PersonCard>
                ))}
              </ListGrid>
            ) : (
              <EmptyState>
                <EmptyStateIcon>
                  <FaClipboardCheck />
                </EmptyStateIcon>
                <EmptyStateTitle>No check-ins yet</EmptyStateTitle>
                <EmptyStateText>
                  No campers have been checked in for this day yet.
                </EmptyStateText>
              </EmptyState>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <FaHistory /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardBody>
            {checkInOverview?.recentCheckIns?.length ? (
              <ActivityList>
                {checkInOverview.recentCheckIns.map((entry) => (
                  <ActivityItem key={`${entry.dayKey}-${entry.participantKey}`}>
                    <strong>{entry.participantName}</strong>
                    <span>
                      {entry.dayLabel} • {entry.registrationNumber}
                    </span>
                    <small>{formatDateTime(entry.checkedInAt)}</small>
                  </ActivityItem>
                ))}
              </ActivityList>
            ) : (
              <EmptyState>
                <EmptyStateIcon>
                  <FaHistory />
                </EmptyStateIcon>
                <EmptyStateTitle>No recent activity</EmptyStateTitle>
                <EmptyStateText>
                  Recent camp check-ins will appear here after the first scan.
                </EmptyStateText>
              </EmptyState>
            )}
          </CardBody>
        </Card>
      </Columns>

      <CampFallbackRegistrationModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onRegistered={handleCamperRegistered}
        suggestedName={nameSearch.trim()}
      />
    </AdminLayout>
  );
};

const LoadingState = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  color: #64748b;
  font-weight: 600;
`;

const ToolbarCard = styled(Card)`
  margin-bottom: 1.5rem;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const DayPicker = styled.div`
  min-width: min(100%, 280px);

  label {
    display: block;
    margin-bottom: 0.45rem;
    color: #334155;
    font-weight: 700;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div`
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 1rem 1.1rem;

  strong,
  span {
    display: block;
  }

  strong {
    color: #0f172a;
    font-size: 1.7rem;
  }

  span {
    margin-top: 0.35rem;
    color: #64748b;
  }
`;

const ResultCard = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  margin-bottom: 1.5rem;
  border-radius: 18px;
  border: 1px solid
    ${(props) =>
      props.$status === 'already_checked_in' ? '#fde68a' : '#bbf7d0'};
  background: ${(props) =>
    props.$status === 'already_checked_in' ? '#fffbeb' : '#ecfdf5'};
`;

const ResultIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: white;
  color: #166534;
  flex-shrink: 0;
`;

const ResultTitle = styled.div`
  color: #0f172a;
  font-weight: 800;
`;

const ResultText = styled.div`
  margin-top: 0.2rem;
  color: #334155;
`;

const ResultMeta = styled.div`
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.9rem;
`;

const SearchHelp = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
`;

const ManualSearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid #dbe2ea;
  background: white;

  svg {
    color: #64748b;
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: #0f172a;
    font-size: 0.95rem;
  }
`;

const SearchHint = styled.div`
  margin-top: 0.9rem;
  color: #64748b;
  font-size: 0.92rem;
`;

const NoMatchBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.9rem;
  flex-wrap: wrap;
`;

const ManualResults = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-top: 1rem;
`;

const ManualResultCard = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;

  strong,
  span,
  small {
    display: block;
  }

  strong {
    color: #0f172a;
  }

  span,
  small {
    margin-top: 0.25rem;
    color: #64748b;
  }

  @media (max-width: 720px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const DayCard = styled.button`
  border-radius: 18px;
  padding: 1rem;
  border: 1px solid ${(props) => (props.$active ? '#bfdbfe' : '#e2e8f0')};
  background: ${(props) =>
    props.$active
      ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'};
  text-align: left;
  cursor: pointer;

  h3,
  p,
  strong,
  span {
    display: block;
    margin: 0;
  }

  h3 {
    color: #0f172a;
    font-size: 1rem;
  }

  p,
  span {
    margin-top: 0.35rem;
    color: #64748b;
  }

  strong {
    margin-top: 0.85rem;
    color: #1d4ed8;
    font-size: 1.5rem;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const ListGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const PersonCard = styled.div`
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0.95rem 1rem;

  strong,
  span,
  small {
    display: block;
  }

  strong {
    color: #0f172a;
  }

  span,
  small {
    margin-top: 0.25rem;
    color: #64748b;
  }
`;

const ActivityList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const ActivityItem = styled.div`
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  strong,
  span,
  small {
    display: block;
  }

  strong {
    color: #0f172a;
  }

  span,
  small {
    margin-top: 0.25rem;
    color: #64748b;
  }
`;

export default CampCheckInsPage;
